import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle, BarChart3, CalendarClock, Check, Copy, Download, FileSpreadsheet,
  FileText, Info, Loader, Play, RefreshCw, Sliders, Table as TableIcon
} from 'lucide-react'
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts'
import { API_CONFIG, apiClient, buildApiUrl } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import SearchableSelect from '../common/SearchableSelect'
import { reportVehicleOption } from './vehicleOption'
import ReportScheduleManager from './ReportScheduleManager'
import type {
  ReportCatalog, ReportDescriptor, ReportRun, ReportScope
} from './reportTypes'

interface ReportsPanelProps {
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm?: (message: string) => Promise<boolean>
  /** Pre-select a vehicle. `token` changes on every request so the same vehicle can be chosen again. */
  initialVehicle?: { source: 'ASSET' | 'CONTAINER', id: number, token: number } | null
}

interface FleetOption { id: number; name: string; customer_name?: string | null }
interface VehicleOption {
  vehicle_id: number
  vehicle_source: 'ASSET' | 'CONTAINER'
  vehicle_code?: string | null
  license_plate?: string | null
  custom_name?: string | null
  customer_name?: string | null
}
interface CustomerOption { id: number; company_name: string; customer_code?: string | null }
interface CorridorOption {
  id: string
  name: string
  width?: number | null
  waypoint_count?: number | string | null
}
interface DriverOption {
  id: number
  name: string
  staffCode?: string | null
  currentAssignment?: { vehicleLabel?: string | null } | null
}

// The export buttons name the document they produce: "PDF" alone told the user
// nothing about what they were about to download.
const EXPORT_FORMATS: Array<{ id: 'csv' | 'xlsx' | 'pdf'; Icon: typeof FileText; labelKey: string }> = [
  { id: 'pdf', Icon: FileText, labelKey: 'reports.format.pdf' },
  { id: 'xlsx', Icon: FileSpreadsheet, labelKey: 'reports.format.xlsx' },
  { id: 'csv', Icon: Download, labelKey: 'reports.format.csv' }
]

// Which measure makes the chart worth looking at for each report, and how rows
// collapse into one bar per vehicle. Reports whose rows are events (stops,
// exceedances, crossings) are counted; measured reports are summed or maxed.
type ChartSeries = { key: string; agg: 'sum' | 'max' | 'count'; labelKey: string }
const CHART_SERIES: Record<string, ChartSeries[]> = {
  paths_driven: [{ key: 'distance_km', agg: 'sum', labelKey: 'reports.chart.distanceKm' }],
  stops: [{ key: 'duration_minutes', agg: 'sum', labelKey: 'reports.chart.stoppedMinutes' }],
  idling: [{ key: 'wasted_minutes', agg: 'sum', labelKey: 'reports.chart.wastedMinutes' }],
  speed_summary: [
    { key: 'peak_speed_kmh', agg: 'max', labelKey: 'reports.chart.peakKmh' },
    { key: 'average_speed_kmh', agg: 'max', labelKey: 'reports.chart.averageKmh' }
  ],
  speed_exceeds: [
    { key: 'peak_speed_kmh', agg: 'count', labelKey: 'reports.chart.events' },
    { key: 'duration_minutes', agg: 'sum', labelKey: 'reports.chart.minutesOverLimit' }
  ],
  mileage: [{ key: 'gps_distance_km', agg: 'sum', labelKey: 'reports.chart.distanceKm' }],
  engine_hours: [
    { key: 'working_hours', agg: 'sum', labelKey: 'reports.chart.productiveHours' },
    { key: 'idle_hours', agg: 'sum', labelKey: 'reports.chart.idleHours' }
  ],
  border_crossings: [{ key: 'crossed_at', agg: 'count', labelKey: 'reports.chart.crossings' }],
  emissions: [{ key: 'co2_kg', agg: 'sum', labelKey: 'reports.chart.co2Kg' }],
  device_emission_calendar: [
    { key: 'days_emitted', agg: 'sum', labelKey: 'reports.chart.daysEmitted' },
    { key: 'days_silent', agg: 'sum', labelKey: 'reports.chart.daysSilent' }
  ]
}
const SERIES_COLORS = ['#2563EB', '#F59E0B']
const CHART_VEHICLE_LIMIT = 12

const cardCls = 'rounded-xl border border-gray-200 bg-white'
const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
const labelCls = 'block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1'

const formatDateTime = (value: any, locale: string) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString(locale)
}

const formatDuration = (minutes: any) => {
  if (minutes === null || minutes === undefined || minutes === '') return '—'
  const total = Number(minutes)
  if (!Number.isFinite(total)) return '—'
  const hours = Math.floor(total / 60)
  const rest = Math.round(total - hours * 60)
  return hours > 0 ? `${hours} h ${String(rest).padStart(2, '0')}` : `${rest} min`
}

/** Blank, never zero: a missing measurement is not a measured nothing. */
const formatCell = (value: any, type: string, decimals: number | undefined, locale: string) => {
  if (value === null || value === undefined || value === '') return '—'
  switch (type) {
    case 'datetime':
      return formatDateTime(value, locale)
    case 'duration':
      return formatDuration(value)
    case 'coordinate': {
      const pair = Array.isArray(value) ? value : [value, null]
      if (pair[0] === null || pair[0] === undefined) return '—'
      return `${Number(pair[0]).toFixed(5)}, ${pair[1] === null || pair[1] === undefined ? '—' : Number(pair[1]).toFixed(5)}`
    }
    case 'number': {
      const numeric = Number(value)
      if (!Number.isFinite(numeric)) return '—'
      return numeric.toLocaleString(locale, {
        minimumFractionDigits: decimals ?? 0,
        maximumFractionDigits: decimals ?? 2
      })
    }
    case 'boolean':
      return value ? '✓' : '—'
    case 'status':
      return value === 'OK' ? 'ok' : value === 'KO' ? '—' : String(value)
    default:
      return String(value)
  }
}

const ReportsPanel: React.FC<ReportsPanelProps> = ({ showMessage, showConfirm, initialVehicle }) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language?.startsWith('fr') ? 'fr' : 'en'
  const { customerId: tenantCustomerId } = useAuth()
  // A tenant account is pinned server-side, so the picker is only meaningful for
  // a platform account that has not entered a customer workspace.
  const canChooseCustomer = tenantCustomerId == null

  const [tab, setTab] = useState<'run' | 'schedules'>('run')
  const [catalog, setCatalog] = useState<ReportCatalog | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  const [selectedReportId, setSelectedReportId] = useState<string>('')
  const [scopeKind, setScopeKind] = useState<ReportScope['kind']>('customer')
  const [fleetId, setFleetId] = useState<string>('')
  const [vehicleRef, setVehicleRef] = useState<string>('')

  useEffect(() => {
    if (!initialVehicle) return
    setTab('run')
    setScopeKind('vehicle')
    setVehicleRef(`${initialVehicle.source}:${initialVehicle.id}`)
  }, [initialVehicle?.token])
  const [period, setPeriod] = useState('LAST_7_DAYS')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  // Optional clock times on a custom period: "8 October, 08:00 to 18:00".
  // Empty means the whole day, which is what the API assumes for a bare date.
  const [fromTime, setFromTime] = useState('')
  const [toTime, setToTime] = useState('')
  const withTime = (day: string, time: string) => {
    if (!day) return undefined
    if (!time) return day
    const instant = new Date(`${day}T${time}`)
    return Number.isNaN(instant.getTime()) ? day : instant.toISOString()
  }
  const [params, setParams] = useState<Record<string, string | boolean>>({})

  const [driverId, setDriverId] = useState<string>('')
  const [corridorId, setCorridorId] = useState<string>('')
  const [customerId, setCustomerId] = useState<string>('')

  const [fleets, setFleets] = useState<FleetOption[]>([])
  const [vehicles, setVehicles] = useState<VehicleOption[]>([])
  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [drivers, setDrivers] = useState<DriverOption[]>([])
  const [corridors, setCorridors] = useState<CorridorOption[]>([])

  const [run, setRun] = useState<ReportRun | null>(null)
  const [running, setRunning] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)
  const [runError, setRunError] = useState<string | null>(null)
  const [copiedReference, setCopiedReference] = useState<string | null>(null)

  // The reference is what a reader quotes when they ask about an export they
  // received, so it has to leave the screen without being retyped.
  const copyReference = useCallback(async (reference: string) => {
    try {
      await navigator.clipboard.writeText(reference)
      setCopiedReference(reference)
      setTimeout(() => setCopiedReference(current => (current === reference ? null : current)), 2000)
    } catch {
      showMessage?.(t('reports.copyFailed'), 'warning')
    }
  }, [showMessage, t])

  const selectedReport: ReportDescriptor | undefined = useMemo(
    () => catalog?.reports.find(report => report.id === selectedReportId),
    [catalog, selectedReportId]
  )

  const fleetOptions = useMemo(() => fleets.map(fleet => ({
    value: String(fleet.id),
    label: fleet.name,
    hint: fleet.customer_name || null
  })), [fleets])

  const customerOptions = useMemo(() => customers.map(customer => ({
    value: String(customer.id),
    label: customer.company_name,
    hint: customer.customer_code || null
  })), [customers])

  const corridorOptions = useMemo(() => corridors
    // A corridor with fewer than two waypoints has no path to measure against.
    .filter(corridor => Number(corridor.waypoint_count ?? 2) >= 2)
    .map(corridor => ({
      value: corridor.id,
      label: corridor.name,
      hint: corridor.width ? `± ${corridor.width} km` : null
    })), [corridors])

  const driverOptions = useMemo(() => drivers.map(driver => ({
    value: String(driver.id),
    label: driver.name,
    hint: [driver.staffCode, driver.currentAssignment?.vehicleLabel].filter(Boolean).join(' · ') || null
  })), [drivers])

  const vehicleOptions = useMemo(() => vehicles.map(reportVehicleOption), [vehicles])

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true)
    setCatalogError(null)
    try {
      // The catalog is customer-specific: which reports are available depends on
      // the signals that customer's trackers actually report.
      const response = await apiClient.get('/reports', customerId ? { locale, customer_id: customerId } : { locale })
      const data = response.data as ReportCatalog
      setCatalog(data)
      if (data.enabled && data.reports.length) {
        setSelectedReportId(current =>
          current && data.reports.some(report => report.id === current) ? current : data.reports[0].id)
      }
    } catch (error: any) {
      setCatalogError(error?.message || 'Failed to load the report catalog')
    } finally {
      setCatalogLoading(false)
    }
  }, [locale, customerId])

  useEffect(() => { loadCatalog() }, [loadCatalog])

  useEffect(() => {
    if (!canChooseCustomer) return
    let cancelled = false
    apiClient.get('/customers')
      .then(response => {
        if (cancelled) return
        const rows = Array.isArray(response.data) ? response.data : (response.data?.customers || [])
        setCustomers(rows)
      })
      .catch(() => { /* Without the list the panel simply stays platform-wide. */ })
    return () => { cancelled = true }
  }, [canChooseCustomer])

  useEffect(() => {
    let cancelled = false
    const params = customerId ? { customer_id: customerId } : undefined
    // A fleet, vehicle or driver picked under another customer is no longer in scope.
    setFleetId('')
    setVehicleRef('')
    setDriverId('')
    setCorridorId('')
    const loadScopeOptions = async () => {
      try {
        const [fleetResponse, vehicleResponse, driverResponse, corridorResponse] = await Promise.all([
          apiClient.get('/fleets', params),
          apiClient.get('/vehicles', params),
          apiClient.get('/drivers', params),
          apiClient.get('/corridors', params)
        ])
        if (cancelled) return
        setCorridors(Array.isArray(corridorResponse.data) ? corridorResponse.data : (corridorResponse.data?.corridors || []))
        setFleets(Array.isArray(fleetResponse.data) ? fleetResponse.data : (fleetResponse.data?.fleets || []))
        const rows = Array.isArray(vehicleResponse.data) ? vehicleResponse.data : (vehicleResponse.data?.vehicles || [])
        setVehicles(rows)
        setDrivers(driverResponse.data?.drivers || [])
      } catch {
        // The scope pickers degrade to "whole account" rather than blocking the
        // panel when either list cannot be read.
      }
    }
    loadScopeOptions()
    return () => { cancelled = true }
  }, [customerId])

  // Parameter defaults follow the selected report (they carry the customer's
  // configured values), and are reset whenever the report changes.
  useEffect(() => {
    if (!selectedReport) return
    const next: Record<string, string | boolean> = {}
    for (const param of selectedReport.params) {
      next[param.key] = param.type === 'boolean'
        ? Boolean(param.default)
        : (param.default === null || param.default === undefined ? '' : String(param.default))
    }
    setParams(next)
    setRun(null)
    setRunError(null)
  }, [selectedReport])

  const buildRequest = () => {
    const scope: ReportScope = { kind: scopeKind }
    if (scopeKind === 'fleet') scope.fleetId = Number(fleetId)
    if (scopeKind === 'vehicle') {
      const [source, id] = vehicleRef.split(':')
      scope.vehicleSource = source as 'ASSET' | 'CONTAINER'
      scope.vehicleId = Number(id)
    }
    if (scopeKind === 'driver') scope.driverId = Number(driverId)
    const numericParams: Record<string, any> = {}
    for (const param of selectedReport?.params || []) {
      const value = params[param.key]
      if (param.type === 'boolean') numericParams[param.key] = Boolean(value)
      else if (value !== '' && value !== undefined) numericParams[param.key] = Number(value)
    }
    return {
      report_id: selectedReportId,
      scope,
      period,
      from: period === 'CUSTOM' ? withTime(from, fromTime) : undefined,
      to: period === 'CUSTOM' ? withTime(to, toTime) : undefined,
      params: numericParams,
      customer_id: customerId || undefined,
      corridor_id: corridorId || undefined,
      locale
    }
  }

  const scopeReady =
    (scopeKind === 'fleet' && fleetId !== '') ||
    (scopeKind === 'vehicle' && vehicleRef !== '') ||
    (scopeKind === 'driver' && driverId !== '') ||
    scopeKind === 'customer'
  const rangeReady = period !== 'CUSTOM' || (from !== '' && to !== '')
  const canRun = Boolean(selectedReportId) && scopeReady && rangeReady && !running

  const handleRun = async () => {
    if (!canRun) return
    setRunning(true)
    setRunError(null)
    try {
      const response = await apiClient.post('/reports/run', buildRequest())
      setRun(response.data as ReportRun)
    } catch (error: any) {
      setRun(null)
      setRunError(error?.message || 'The report could not be produced')
      showMessage?.(error?.message || t('reports.runFailed'), 'error')
    } finally {
      setRunning(false)
    }
  }

  // Exports are binary, so they bypass apiClient (which parses JSON) and stream
  // straight into a download.
  const handleExport = async (format: 'csv' | 'xlsx' | 'pdf') => {
    if (!selectedReportId) return
    setExporting(format)
    try {
      const token = localStorage.getItem('fleet_access_token')
      const response = await fetch(buildApiUrl('/reports/export'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ ...buildRequest(), format }),
        signal: AbortSignal.timeout(API_CONFIG.MUTATION_TIMEOUT)
      })
      if (!response.ok) {
        const detail = await response.text()
        let message = `HTTP ${response.status}`
        try { message = JSON.parse(detail).error || message } catch { /* keep status */ }
        throw new Error(message)
      }
      const disposition = response.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename="([^"]+)"/)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = match ? match[1] : `${selectedReportId}.${format}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      showMessage?.(t('reports.exportReady', { format: format.toUpperCase() }), 'success')
    } catch (error: any) {
      showMessage?.(error?.message || t('reports.exportFailed'), 'error')
    } finally {
      setExporting(null)
    }
  }

  const chartData = useMemo(() => {
    if (!run || !run.rows.length) return []
    const series = CHART_SERIES[run.report.id]
    if (!series) return []
    const byVehicle = new Map<string, Record<string, number>>()
    for (const row of run.rows) {
      const name = String(row.vehicle ?? '—')
      const bucket = byVehicle.get(name) || {}
      for (const entry of series) {
        const current = bucket[entry.key]
        if (entry.agg === 'count') {
          bucket[entry.key] = (current || 0) + 1
        } else {
          const value = Number(row[entry.key])
          if (!Number.isFinite(value)) continue
          bucket[entry.key] = entry.agg === 'max'
            ? Math.max(current ?? value, value)
            : (current || 0) + value
        }
      }
      byVehicle.set(name, bucket)
    }
    const primary = series[0].key
    return [...byVehicle.entries()]
      .map(([name, values]) => ({ name, ...values }))
      .sort((a, b) => Number((b as any)[primary] || 0) - Number((a as any)[primary] || 0))
      .slice(0, CHART_VEHICLE_LIMIT)
  }, [run])

  const chartSeries = run ? (CHART_SERIES[run.report.id] || []) : []

  const grouped = useMemo(() => {
    const categories = catalog?.categories || []
    return categories
      .map(category => ({
        ...category,
        reports: (catalog?.reports || []).filter(report => report.category === category.id)
      }))
      .filter(category => category.reports.length > 0)
  }, [catalog])

  if (catalogLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 p-6 text-sm text-gray-500">
        <Loader size={16} className="animate-spin" />{t('loading')}
      </div>
    )
  }

  if (catalogError) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">{t('reports.catalogFailed')}</p>
            <p className="mt-1 text-xs">{catalogError}</p>
          </div>
          <button onClick={loadCatalog} className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold">
            <RefreshCw size={13} />{t('retry')}
          </button>
        </div>
      </div>
    )
  }

  if (catalog && !catalog.enabled) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <Info size={18} className="mt-0.5 flex-shrink-0" />
          <p>{t('reports.disabled')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50">
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-gray-200 bg-white px-4 pt-3">
        {([
          { id: 'run' as const, label: t('reports.tabRun'), Icon: BarChart3 },
          { id: 'schedules' as const, label: t('reports.tabSchedules'), Icon: CalendarClock }
        ]).map(item => (
          <button
            key={item.id}
            onClick={() => setTab(item.id)}
            className={`flex items-center gap-2 rounded-t-lg px-4 py-2 text-sm font-bold transition-colors ${
              tab === item.id ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <item.Icon size={15} />{item.label}
          </button>
        ))}
      </div>

      {tab === 'schedules' ? (
        <ReportScheduleManager
          reports={catalog?.reports || []}
          periods={(catalog?.periods || []).filter(item => item !== 'CUSTOM')}
          formats={catalog?.formats || ['pdf']}
          fleets={fleets}
          vehicles={vehicles}
          drivers={drivers}
          customers={customers}
          canChooseCustomer={canChooseCustomer}
          showMessage={showMessage}
          showConfirm={showConfirm}
        />
      ) : (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Report picker */}
          <aside className="w-64 flex-shrink-0 overflow-y-auto border-r border-gray-200 bg-white p-3">
            {grouped.map(category => (
              <div key={category.id} className="mb-4">
                <p className="mb-1.5 px-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">{category.label}</p>
                <div className="space-y-1">
                  {category.reports.map(report => (
                    <button
                      key={report.id}
                      onClick={() => setSelectedReportId(report.id)}
                      className={`w-full rounded-lg px-2.5 py-2 text-left transition-colors ${
                        selectedReportId === report.id ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className={`block text-sm font-semibold ${report.available ? 'text-gray-800' : 'text-gray-400'}`}>
                        {report.label}
                      </span>
                      {!report.available && (
                        <span className="mt-0.5 block text-[10px] font-semibold text-amber-600">
                          {t('reports.missingSignals', {
                            signals: report.missingSignals.map(signal => t(`reports.signal.${signal}`)).join(', ')
                          })}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          <div className="min-w-0 flex-1 overflow-y-auto p-4">
            {selectedReport && (
              <>
                <div className={`${cardCls} mb-4 p-4`}>
                  <h3 className="text-base font-bold text-gray-800">{selectedReport.label}</h3>
                  <p className="mt-0.5 text-xs text-gray-500">{selectedReport.description}</p>

                  {!selectedReport.available && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                      <span>
                        {t('reports.signalWarning', {
                          signals: selectedReport.missingSignals.map(signal => t(`reports.signal.${signal}`)).join(', ')
                        })}
                      </span>
                    </div>
                  )}

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                    {canChooseCustomer && (
                      <div>
                        <label className={labelCls}>{t('reports.customer')}</label>
                        <SearchableSelect
                          options={customerOptions}
                          value={customerId}
                          onChange={setCustomerId}
                          placeholder={t('reports.allCustomers')}
                          searchPlaceholder={t('reports.searchCustomer')}
                          emptyText={t('reports.noMatch')}
                          clearText={t('reports.allCustomers')}
                        />
                      </div>
                    )}

                    <div>
                      <label className={labelCls}>{t('reports.scope')}</label>
                      <select value={scopeKind} onChange={event => setScopeKind(event.target.value as ReportScope['kind'])} className={inputCls}>
                        <option value="customer">{t('reports.scopeCustomer')}</option>
                        <option value="fleet">{t('reports.scopeFleet')}</option>
                        <option value="vehicle">{t('reports.scopeVehicle')}</option>
                        <option value="driver">{t('reports.scopeDriver')}</option>
                      </select>
                    </div>

                    {scopeKind === 'fleet' && (
                      <div>
                        <label className={labelCls}>{t('reports.fleet')}</label>
                        <SearchableSelect
                          options={fleetOptions}
                          value={fleetId}
                          onChange={setFleetId}
                          placeholder={t('reports.choose')}
                          searchPlaceholder={t('reports.searchFleet')}
                          emptyText={t('reports.noMatch')}
                        />
                      </div>
                    )}

                    {scopeKind === 'vehicle' && (
                      <div>
                        <label className={labelCls}>{t('reports.vehicle')}</label>
                        <SearchableSelect
                          options={vehicleOptions}
                          value={vehicleRef}
                          onChange={setVehicleRef}
                          placeholder={t('reports.choose')}
                          searchPlaceholder={t('reports.searchVehicle')}
                          emptyText={t('reports.noMatch')}
                        />
                      </div>
                    )}

                    {scopeKind === 'driver' && (
                      <div>
                        <label className={labelCls}>{t('reports.driver')}</label>
                        <SearchableSelect
                          options={driverOptions}
                          value={driverId}
                          onChange={setDriverId}
                          placeholder={t('reports.choose')}
                          searchPlaceholder={t('reports.searchDriver')}
                          emptyText={t('reports.noDrivers')}
                        />
                        <p className="mt-1 text-[11px] text-gray-400">{t('reports.driverHint')}</p>
                      </div>
                    )}

                    <div>
                      <label className={labelCls}>{t('reports.periodLabel')}</label>
                      <select value={period} onChange={event => setPeriod(event.target.value)} className={inputCls}>
                        {(catalog?.periods || []).map(item => (
                          <option key={item} value={item}>{t(`reports.periods.${item}`)}</option>
                        ))}
                      </select>
                    </div>

                    {corridorOptions.length > 0 && (
                      <div>
                        <label className={labelCls}>{t('reports.corridor')}</label>
                        <SearchableSelect
                          options={corridorOptions}
                          value={corridorId}
                          onChange={setCorridorId}
                          placeholder={t('reports.corridorAny')}
                          searchPlaceholder={t('reports.searchCorridor')}
                          emptyText={t('reports.noMatch')}
                          clearText={t('reports.corridorAny')}
                        />
                        {corridorId && (
                          <p className="mt-1 text-[11px] text-blue-600">{t('reports.corridorHint')}</p>
                        )}
                      </div>
                    )}

                    {period === 'CUSTOM' && (
                      <>
                        <div>
                          <label className={labelCls}>{t('reports.from')}</label>
                          <div className="flex gap-2">
                            <input type="date" value={from} onChange={event => setFrom(event.target.value)} className={inputCls} />
                            <input
                              type="time"
                              value={fromTime}
                              onChange={event => setFromTime(event.target.value)}
                              className={`${inputCls} max-w-[7.5rem]`}
                              title={t('reports.fromTimeHint', { defaultValue: 'Start time (empty: from midnight)' })}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={labelCls}>{t('reports.to')}</label>
                          <div className="flex gap-2">
                            <input type="date" value={to} onChange={event => setTo(event.target.value)} className={inputCls} />
                            <input
                              type="time"
                              value={toTime}
                              onChange={event => setToTime(event.target.value)}
                              className={`${inputCls} max-w-[7.5rem]`}
                              title={t('reports.toTimeHint', { defaultValue: 'End time (empty: until the end of the day)' })}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {selectedReport.params.map(param => (
                      <div key={param.key}>
                        <label className={labelCls}>
                          {param.label}{param.unit ? ` (${param.unit})` : ''}
                        </label>
                        {param.type === 'boolean' ? (
                          <label className="flex items-center gap-2 py-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={Boolean(params[param.key])}
                              onChange={event => setParams(current => ({ ...current, [param.key]: event.target.checked }))}
                            />
                            {t('enabled')}
                          </label>
                        ) : (
                          <input
                            type="number"
                            min={param.min}
                            max={param.max}
                            value={String(params[param.key] ?? '')}
                            onChange={event => setParams(current => ({ ...current, [param.key]: event.target.value }))}
                            className={inputCls}
                          />
                        )}
                        {param.hint && <p className="mt-1 text-[11px] leading-snug text-gray-500">{param.hint}</p>}
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      onClick={handleRun}
                      disabled={!canRun}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                    >
                      {running ? <Loader size={14} className="animate-spin" /> : <Play size={14} />}
                      {t('reports.run')}
                    </button>
                    {EXPORT_FORMATS.map(format => (
                      <button
                        key={format.id}
                        onClick={() => handleExport(format.id)}
                        disabled={!canRun && !run}
                        className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {exporting === format.id
                          ? <Loader size={14} className="animate-spin" />
                          : <format.Icon size={14} />}
                        {t(format.labelKey)}
                      </button>
                    ))}
                    {selectedReport.params.length > 0 && (
                      <span className="flex items-center gap-1 text-[11px] text-gray-400">
                        <Sliders size={12} />{t('reports.paramsHint')}
                      </span>
                    )}
                  </div>
                </div>

                {runError && (
                  <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />{runError}
                  </div>
                )}

                {run && (
                  <>
                    <div className={`${cardCls} mb-4 p-4`}>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-500">
                        {run.reference && (
                          <button
                            type="button"
                            onClick={() => copyReference(run.reference as string)}
                            title={t('reports.copyReference')}
                            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-[11px] text-gray-700 hover:border-blue-300 hover:text-blue-700"
                          >
                            {run.reference}
                            {copiedReference === run.reference
                              ? <Check size={12} className="text-green-600" />
                              : <Copy size={12} className="text-gray-400" />}
                          </button>
                        )}
                        <span>
                          <strong className="text-gray-700">{t('reports.rangeLabel')}:</strong>{' '}
                          {formatDateTime(run.range.from, locale)} → {formatDateTime(run.range.to, locale)}
                        </span>
                        <span>
                          <strong className="text-gray-700">{t('reports.scope')}:</strong>{' '}
                          {run.scope.label || t(`reports.scope${run.scope.kind.charAt(0).toUpperCase()}${run.scope.kind.slice(1)}`)}
                          {run.scope.vehicleCount != null && ` (${t('reports.vehicleCount', { count: run.scope.vehicleCount })})`}
                        </span>
                        <span>
                          <strong className="text-gray-700">{t('reports.rowCount')}:</strong> {run.total.toLocaleString(locale)}
                        </span>
                        {run.corridor && (
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
                            <strong>{t('reports.corridor')}:</strong>{' '}
                            {run.corridor.name} ({t('reports.corridorWidthKm', {
                              width: (run.corridor.widthMetres / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })
                            })})
                          </span>
                        )}
                      </div>

                      {Object.keys(run.summary).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {Object.entries(run.summary).map(([key, value]) => (
                            <div key={key} className="rounded-lg bg-slate-50 px-3 py-2">
                              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                                {t(`reports.summary.${key}`, { defaultValue: key.replace(/_/g, ' ') })}
                              </p>
                              <p className="text-sm font-bold text-gray-800">
                                {value === null || value === undefined
                                  ? '—'
                                  : /_minutes$/.test(key)
                                    ? formatDuration(value)
                                    : Number(value).toLocaleString(locale)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {run.truncated && (
                        <p className="mt-3 text-[11px] font-semibold text-amber-600">{t('reports.truncated')}</p>
                      )}
                    </div>

                    {chartData.length > 0 && (
                      <div className={`${cardCls} mb-4 p-4`}>
                        <p className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-800">
                          <BarChart3 size={15} className="text-blue-600" />
                          {t('reports.chartTitle', { count: chartData.length })}
                        </p>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 44 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} height={56} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              {chartSeries.map((entry, index) => (
                                <Bar
                                  key={entry.key}
                                  dataKey={entry.key}
                                  name={t(entry.labelKey)}
                                  fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                                  radius={[3, 3, 0, 0]}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {run.notices.length > 0 && (
                      <div className={`${cardCls} mb-4 border-amber-200 bg-amber-50 p-4`}>
                        <p className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-800">
                          <Info size={15} />{t('reports.noticesTitle')}
                        </p>
                        <ul className="space-y-1 text-xs text-amber-800">
                          {run.notices.map((notice, index) => (
                            <li key={`${notice.vehicleKey || notice.vehicle}-${index}`}>
                              <strong>{notice.vehicle || notice.vehicleKey}</strong>{' — '}
                              {t(`reports.notice.${notice.reason}`, { defaultValue: notice.reason })}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className={`${cardCls} overflow-hidden`}>
                      <p className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 text-sm font-bold text-gray-800">
                        <TableIcon size={15} className="text-gray-400" />{run.report.label}
                      </p>
                      {run.rows.length === 0 ? (
                        <p className="p-6 text-center text-sm italic text-gray-500">{t('reports.noRows')}</p>
                      ) : (
                        <div className="max-h-[420px] overflow-auto">
                          <table className="min-w-full text-left text-xs">
                            <thead className="sticky top-0 bg-gray-50">
                              <tr>
                                {run.columns.map(column => (
                                  <th key={column.key} className="whitespace-nowrap px-3 py-2 font-bold text-gray-600">
                                    {column.label}{column.unit ? ` (${column.unit})` : ''}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {run.rows.map((row, index) => (
                                <tr key={index} className="border-t border-gray-100 hover:bg-slate-50">
                                  {run.columns.map(column => (
                                    column.type === 'status' ? (
                                      // Transmission calendar: a coloured square per day, blue when the
                                      // tracker sent something, red when it stayed silent.
                                      <td key={column.key} className="px-1 py-1.5" title={column.label}>
                                        <span
                                          className={`block h-4 w-4 rounded-sm ${row[column.key] === 'OK' ? 'bg-blue-600' : row[column.key] === 'KO' ? 'bg-red-600' : 'bg-gray-200'}`}
                                        />
                                      </td>
                                    ) : (
                                      <td key={column.key} className="whitespace-nowrap px-3 py-1.5 text-gray-700">
                                        {formatCell(row[column.key], column.type, column.decimals, locale)}
                                      </td>
                                    )
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportsPanel
